import React, { useState } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink } from "react-router-dom";
import {
    FiTrendingUp,
    FiTrendingDown,
    FiDownload,
    FiRefreshCw,
    FiCalendar,
    FiFileText,
    FiAlertCircle,
    FiLock,
    FiStar,
} from "react-icons/fi";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

// Format number in Indian Rupees
const formatINR = (amount) => {
    const numAmount = typeof amount === "number" ? amount : parseFloat(amount) || 0;
    return `₹${Number(numAmount.toFixed(0)).toLocaleString("en-IN")}`;
};

// Format percentage
const formatPercent = (value) => {
    return `${value.toFixed(2)}%`;
};

// Format date
const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

export default function ModelPortfolio() {
    const [subscriptionPlan, setSubscriptionPlan] = useState("premium"); // free, basic, premium
    const [selectedView, setSelectedView] = useState("allocation"); // allocation, changes, notes

    // Static Data - Portfolio Allocation
    const portfolioAllocation = [
        { name: "Large Cap", value: 40, amount: 400000, color: "#1bafb4" },
        { name: "Mid Cap", value: 30, amount: 300000, color: "#06b6a4" },
        { name: "Small Cap", value: 15, amount: 150000, color: "#10b981" },
        { name: "Debt", value: 10, amount: 100000, color: "#3b82f6" },
        { name: "Gold", value: 5, amount: 50000, color: "#f59e0b" },
    ];

    const totalPortfolio = portfolioAllocation.reduce((sum, item) => sum + item.amount, 0);

    // Static Data - Change Log
    const changeLog = [
        {
            id: 1,
            date: "2024-01-15",
            type: "Rebalance",
            changes: [
                { category: "Large Cap", oldValue: 35, newValue: 40, change: +5 },
                { category: "Mid Cap", oldValue: 35, newValue: 30, change: -5 },
            ],
            reason: "Market conditions favor large cap stocks",
        },
        {
            id: 2,
            date: "2024-01-01",
            type: "Rebalance",
            changes: [
                { category: "Debt", oldValue: 15, newValue: 10, change: -5 },
                { category: "Small Cap", oldValue: 10, newValue: 15, change: +5 },
            ],
            reason: "Increased risk appetite for growth",
        },
        {
            id: 3,
            date: "2023-12-15",
            type: "Initial",
            changes: [
                { category: "Large Cap", oldValue: 0, newValue: 35, change: 35 },
                { category: "Mid Cap", oldValue: 0, newValue: 35, change: 35 },
                { category: "Small Cap", oldValue: 0, newValue: 10, change: 10 },
                { category: "Debt", oldValue: 0, newValue: 15, change: 15 },
                { category: "Gold", oldValue: 0, newValue: 5, change: 5 },
            ],
            reason: "Initial portfolio allocation",
        },
    ];

    // Static Data - Rebalance Notes
    const rebalanceNotes = [
        {
            id: 1,
            date: "2024-01-15",
            title: "Q1 2024 Rebalancing Strategy",
            content: "Based on current market conditions and economic outlook, we recommend increasing exposure to large cap stocks while reducing mid cap allocation. This rebalancing reflects our view that large caps offer better risk-adjusted returns in the current environment.",
            author: "Portfolio Manager",
        },
        {
            id: 2,
            date: "2024-01-01",
            title: "New Year Portfolio Adjustments",
            content: "Starting the new year with a slight shift towards growth-oriented assets. Reducing debt allocation in favor of small cap exposure to capture potential upside while maintaining overall portfolio balance.",
            author: "Investment Advisor",
        },
    ];

    const canAccessContent = () => {
        return subscriptionPlan !== "free";
    };

    const handleExport = () => {
        alert("Exporting portfolio allocation data...");
    };

    if (!canAccessContent()) {
        return (
            <>
                <ClientNavbar />
                <div className="page-content" style={{ background: "#f8fafc" }}>
                        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px" }}>
                            <div className="card-body p-4">
                                <div className="text-center py-5">
                                    <div
                                        className="mb-4 d-inline-flex align-items-center justify-content-center"
                                        style={{
                                            width: "80px",
                                            height: "80px",
                                            borderRadius: "50%",
                                            background: "#c8eeecbf",
                                        }}
                                    >
                                        <FiLock style={{ fontSize: "40px", color: "#39b2b4" }} />
                                    </div>
                                    <h4 className="fw-bold mb-3" style={{ color: "#006e82" }}>Premium Feature</h4>
                                    <p className="mb-4" style={{ color: "#64748b", fontSize: "15px" }}>
                                        Model Portfolio is available for Premium plan subscribers only.
                                    </p>
                                    <button
                                        className="btn fw-semibold px-4"
                                        onClick={() => window.location.href = "/research/plans-billing"}
                                        style={{
                                            borderRadius: "10px",
                                            background: "linear-gradient(135deg, #39b2b4 0%, #06b6a4 100%)",
                                            color: "white",
                                            border: "none",
                                            padding: "12px 24px",
                                        }}
                                    >
                                        <FiStar className="me-2" />
                                        Upgrade to Premium
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
            </>
        );
    }

    return (
        <>
            <ClientNavbar />
            <div className="page-content" style={{ background: "#f8fafc" }}>
                    {/* Header */}
                    <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px" }}>
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-center flex-wrap">
                                <div>
                                    <h3 className="text-primary mb-2">Model Portfolio</h3>
                                    <nav
                                        aria-label="breadcrumb"
                                        style={{ "--bs-breadcrumb-divider": "none" }}
                                    >
                                        <ol className="breadcrumb mb-0">
                                            <li className="breadcrumb-item">
                                                <NavLink to="/overview">Dashboard</NavLink> /
                                            </li>
                                            <li className="breadcrumb-item">
                                                <NavLink to="/overview">Research Analysis</NavLink> /
                                            </li>
                                            <li className="breadcrumb-item active">Model Portfolio</li>
                                        </ol>
                                    </nav>
                                    
                                </div>
                                <div className="d-flex gap-2 align-items-center flex-wrap mt-2">
                                    <button
                                        className="btn btn-primary btn-sm "
                                        onClick={handleExport}
                                     
                                    >
                                        {/* <FiDownload className="me-1" /> */}
                                        Export
                                    </button>
                                    <span
                                        className="badge px-3 py-2 fw-semibold"
                                        style={{
                                            fontSize: "12px",
                                            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                            color: "white",
                                        }}
                                    >
                                        Premium Plan
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
                        <div className="card-body p-0">
                            <ul className="nav d-flex  nav-tabs border-0 px-3 pt-3">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${selectedView === "allocation" ? "active" : ""}`}
                                        onClick={() => setSelectedView("allocation")}
                                        style={{
                                            border: "none",
                                            padding: "12px 24px",
                                            fontWeight: selectedView === "allocation" ? "600" : "500",
                                            borderRadius: "10px 10px 0 0",
                                            color: selectedView === "allocation" ? "#006e82" : "#64748b",
                                            backgroundColor: selectedView === "allocation" ? "#ccf1f7" : "transparent",
                                            borderBottom: selectedView === "allocation" ? "3px solid #39b2b4" : "none",
                                            transition: "all 0.3s ease",
                                        }}
                                    >
                                        Allocation
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${selectedView === "changes" ? "active" : ""}`}
                                        onClick={() => setSelectedView("changes")}
                                        style={{
                                            border: "none",
                                            padding: "12px 24px",
                                            fontWeight: selectedView === "changes" ? "600" : "500",
                                            borderRadius: "10px 10px 0 0",
                                            color: selectedView === "changes" ? "#006e82" : "#64748b",
                                            backgroundColor: selectedView === "changes" ? "#ccf1f7" : "transparent",
                                            borderBottom: selectedView === "changes" ? "3px solid #39b2b4" : "none",
                                            transition: "all 0.3s ease",
                                        }}
                                    >
                                        Change Log
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${selectedView === "notes" ? "active" : ""}`}
                                        onClick={() => setSelectedView("notes")}
                                        style={{
                                            border: "none",
                                            padding: "12px 24px",
                                            fontWeight: selectedView === "notes" ? "600" : "500",
                                            borderRadius: "10px 10px 0 0",
                                            color: selectedView === "notes" ? "#006e82" : "#64748b",
                                            backgroundColor: selectedView === "notes" ? "#ccf1f7" : "transparent",
                                            borderBottom: selectedView === "notes" ? "3px solid #39b2b4" : "none",
                                            transition: "all 0.3s ease",
                                        }}
                                    >
                                        Rebalance Notes
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Allocation View */}
                    {selectedView === "allocation" && (
                        <>
                            <div className="row g-4 mb-4">
                                {/* Pie Chart */}
                                <div className="col-lg-6">
                                    <div className="card shadow-sm border-0" style={{ borderRadius: "12px" }}>
                                        <div
                                            className="card-header border-0 d-flex align-items-center py-3 px-4"
                                            style={{
                                                background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                                borderBottom: "1px solid #b3eaf4",
                                            }}
                                        >
                                            <div
                                                className="me-3 d-flex align-items-center justify-content-center"
                                                style={{
                                                    width: "40px",
                                                    height: "40px",
                                                    borderRadius: "10px",
                                                    background: "#c8eeecbf",
                                                }}
                                            >
                                                <FiFileText style={{ color: "#39b2b4", fontSize: "20px" }} />
                                            </div>
                                            <h5 className="mb-0 fw-bold" style={{ color: "#006e82" }}>Portfolio Allocation</h5>
                                        </div>
                                        <div className="card-body p-4">
                                            <ResponsiveContainer width="100%" height={300}>
                                                <PieChart>
                                                    <Pie
                                                        data={portfolioAllocation}
                                                        cx="50%"
                                                        cy="50%"
                                                        labelLine={false}
                                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                        outerRadius={100}
                                                        fill="#8884d8"
                                                        dataKey="value"
                                                    >
                                                        {portfolioAllocation.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip />
                                                    <Legend />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </div>

                                {/* Allocation Table */}
                                <div className="col-lg-6">
                                    <div className="card shadow-sm border-0" style={{ borderRadius: "12px" }}>
                                        <div
                                            className="card-header border-0 d-flex justify-content-between align-items-center py-3 px-4"
                                            style={{
                                                background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                                borderBottom: "1px solid #b3eaf4",
                                            }}
                                        >
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="me-3 d-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: "40px",
                                                        height: "40px",
                                                        borderRadius: "10px",
                                                        background: "#c8eeecbf",
                                                    }}
                                                >
                                                    <FiFileText style={{ color: "#39b2b4", fontSize: "20px" }} />
                                                </div>
                                                <h5 className="mb-0 fw-bold" style={{ color: "#006e82" }}>Allocation Details</h5>
                                            </div>
                                            <span
                                                className="badge px-3 py-2 fw-semibold"
                                                style={{
                                                    fontSize: "12px",
                                                    background: "#39b2b4",
                                                    color: "white",
                                                }}
                                            >
                                                Total: {formatINR(totalPortfolio)}
                                            </span>
                                        </div>
                                        <div className="card-body p-0">
                                            <div className="table-responsive">
                                                <table className="table table-hover mb-0">
                                                    <thead style={{ backgroundColor: "#ccf1f7" }}>
                                                        <tr>
                                                            <th className="fw-semibold" style={{ padding: "16px 20px", color: "#006e82" }}>
                                                                Category
                                                            </th>
                                                            <th className="text-end fw-semibold" style={{ padding: "16px 20px", color: "#006e82" }}>
                                                                Allocation
                                                            </th>
                                                            <th className="text-end fw-semibold" style={{ padding: "16px 20px", color: "#006e82" }}>
                                                                Amount
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {portfolioAllocation.map((item, index) => (
                                                            <tr
                                                                key={index}
                                                                style={{
                                                                    transition: "background-color 0.2s ease",
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.backgroundColor = "#f0f9ff";
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.backgroundColor = "transparent";
                                                                }}
                                                            >
                                                                <td style={{ padding: "16px 20px" }}>
                                                                    <div className="d-flex align-items-center">
                                                                        <div
                                                                            className="me-2"
                                                                            style={{
                                                                                width: "16px",
                                                                                height: "16px",
                                                                                borderRadius: "4px",
                                                                                background: item.color,
                                                                            }}
                                                                        />
                                                                        <strong style={{ color: "#006e82" }}>{item.name}</strong>
                                                                    </div>
                                                                </td>
                                                                <td className="text-end fw-semibold" style={{ padding: "16px 20px", color: "#006e82" }}>
                                                                    {formatPercent(item.value)}
                                                                </td>
                                                                <td className="text-end fw-semibold" style={{ padding: "16px 20px", color: "#006e82" }}>
                                                                    {formatINR(item.amount)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Change Log View */}
                    {selectedView === "changes" && (
                        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px" }}>
                            <div
                                className="card-header border-0 d-flex align-items-center py-3 px-4"
                                style={{
                                    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                    borderBottom: "1px solid #b3eaf4",
                                }}
                            >
                                <div
                                    className="me-3 d-flex align-items-center justify-content-center"
                                    style={{
                                        width: "40px",
                                        height: "40px",
                                        borderRadius: "10px",
                                        background: "#c8eeecbf",
                                    }}
                                >
                                    <FiRefreshCw style={{ color: "#39b2b4", fontSize: "20px" }} />
                                </div>
                                <h5 className="mb-0 fw-bold" style={{ color: "#006e82" }}>Portfolio Change Log</h5>
                            </div>
                            <div className="card-body p-0">
                                {changeLog.map((log) => (
                                    <div key={log.id} className="border-bottom p-4">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                    <span
                                                        className="badge px-3 py-2 fw-semibold"
                                                        style={{
                                                            fontSize: "12px",
                                                            background: "#39b2b4",
                                                            color: "white",
                                                        }}
                                                    >
                                                        {log.type}
                                                    </span>
                                                    <span style={{ color: "#64748b", fontSize: "13px" }}>
                                                        <FiCalendar className="me-1" style={{ color: "#94a3b8" }} />
                                                        {formatDate(log.date)}
                                                    </span>
                                                </div>
                                                <h6 className="fw-bold mb-1" style={{ color: "#006e82" }}>{log.reason}</h6>
                                            </div>
                                        </div>
                                        <div className="table-responsive">
                                            <table className="table table-sm mb-0">
                                                <thead style={{ backgroundColor: "#ccf1f7" }}>
                                                    <tr>
                                                        <th style={{ color: "#006e82", fontWeight: "600" }}>Category</th>
                                                        <th className="text-end" style={{ color: "#006e82", fontWeight: "600" }}>Old</th>
                                                        <th className="text-end" style={{ color: "#006e82", fontWeight: "600" }}>New</th>
                                                        <th className="text-end" style={{ color: "#006e82", fontWeight: "600" }}>Change</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {log.changes.map((change, index) => (
                                                        <tr
                                                            key={index}
                                                            style={{
                                                                transition: "background-color 0.2s ease",
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.backgroundColor = "#f0f9ff";
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.backgroundColor = "transparent";
                                                            }}
                                                        >
                                                            <td style={{ color: "#006e82", fontWeight: "500" }}>{change.category}</td>
                                                            <td className="text-end" style={{ color: "#64748b" }}>{formatPercent(change.oldValue)}</td>
                                                            <td className="text-end fw-semibold" style={{ color: "#006e82" }}>{formatPercent(change.newValue)}</td>
                                                            <td className="text-end">
                                                                <span
                                                                    className="d-inline-flex align-items-center fw-semibold"
                                                                    style={{
                                                                        color: change.change > 0 ? "#10b981" : "#ef4444",
                                                                    }}
                                                                >
                                                                    {change.change > 0 ? (
                                                                        <FiTrendingUp className="me-1" />
                                                                    ) : (
                                                                        <FiTrendingDown className="me-1" />
                                                                    )}
                                                                    {change.change > 0 ? "+" : ""}
                                                                    {formatPercent(change.change)}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Rebalance Notes View */}
                    {selectedView === "notes" && (
                        <div className="row g-4">
                            {rebalanceNotes.map((note) => (
                                <div key={note.id} className="col-12">
                                    <div className="card shadow-sm border-0" style={{ borderRadius: "12px" }}>
                                        <div
                                            className="card-header border-0 d-flex justify-content-between align-items-center py-3 px-4"
                                            style={{
                                                background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                                borderBottom: "1px solid #b3eaf4",
                                            }}
                                        >
                                            <h5 className="mb-0 fw-bold" style={{ color: "#006e82" }}>{note.title}</h5>
                                            <div className="d-flex align-items-center gap-2">
                                                <span style={{ color: "#64748b", fontSize: "13px" }}>
                                                    <FiCalendar className="me-1" style={{ color: "#94a3b8" }} />
                                                    {formatDate(note.date)}
                                                </span>
                                                <span
                                                    className="badge px-3 py-2 fw-semibold"
                                                    style={{
                                                        fontSize: "12px",
                                                        background: "#39b2b4",
                                                        color: "white",
                                                    }}
                                                >
                                                    {note.author}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="card-body p-4">
                                            <p className="mb-0" style={{ lineHeight: "1.8", fontSize: "15px", color: "#64748b" }}>
                                                {note.content}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Disclaimer */}
                    <div
                        className="alert mt-4 border-0"
                        style={{
                            borderRadius: "12px",
                            backgroundColor: "#ccf1f7",
                            border: "1px solid #b3eaf4",
                            color: "#006e82",
                        }}
                    >
                        <div className="d-flex align-items-start">
                            <FiAlertCircle className="me-3 mt-1" style={{ fontSize: "24px", color: "#006e82" }} />
                            <div>
                                <strong style={{ fontSize: "16px", color: "#006e82" }}>Disclaimer:</strong>
                                <div className="mt-2" style={{ fontSize: "14px", color: "#006e82", opacity: 0.9 }}>
                                    This model portfolio is for informational purposes only and does not constitute investment advice.
                                    Past performance is not indicative of future results. Please consult with a financial advisor before
                                    making investment decisions.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
        </>
    );
}
